import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LoginPage.css";
import APP_VERSION from "../version";


export default function RidesPage({ currentUser, setCurrentUser }) {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCarForm, setShowCarForm] = useState(false);
  const [showWatercraftForm, setShowWatercraftForm] = useState(false);
  const [showTrailerForm, setShowTrailerForm] = useState(false);
  const [newCar, setNewCar] = useState({ make: "", model: "", colour: "", registration: "", drivetrain: "" });
  const [newWatercraft, setNewWatercraft] = useState({ boat_type: "", description: "" });
  const [newTrailer, setNewTrailer] = useState({ trailer_type: "", description: "" });
  const [editingRideId, setEditingRideId] = useState(null);
  const [editedRide, setEditedRide] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate("/login", { replace: true });
    }
  }, [currentUser, navigate]);
  
  const handleLogout = async () => {
    await fetch("/auth/logout", { credentials: "include" });
    setCurrentUser(null);
    navigate("/login", { replace: true });
  };
  

  const fetchRides = async () => {
    try {
      const res = await fetch("/rides", { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load rides");
      setRides(data);
    } catch (err) {
      console.error("❌ Ride fetch error:", err);
      setError("Failed to load your rides");
    } finally {
      setLoading(false);
    }
  };

  const deleteRide = async (rideId) => {
    const confirmed = window.confirm("Are you sure you want to delete this ride?");
    if (!confirmed) return;

    try {
      const res = await fetch(`/rides/${rideId}`, { method: "DELETE", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete ride");

      setRides((prevRides) => prevRides.filter((ride) => ride.id !== rideId));
    } catch (err) {
      console.error("❌ Delete ride error:", err);
      setError("Could not delete ride");
    }
  };

  const saveEditedRide = async () => {
    try {
      const res = await fetch(`/rides/${editingRideId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedRide),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update ride");

      setRides(rides.map((r) => (r.id === editingRideId ? data : r)));
      setEditingRideId(null);
    } catch (err) {
      console.error("❌ Save edit error:", err);
      setError("Could not update ride");
    }
  };

  const handleSaveCar = async () => {
    console.log("🚗 Attempting to save car:", newCar);

    try {
      const response = await fetch("/rides", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "car",
          make: newCar.make,
          model: newCar.model,
          colour: newCar.colour,
          registration: newCar.registration || null,
          drivetrain: newCar.drivetrain,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ Failed to save car:", result.error);
        setError(result.error || "Failed to save car");
      } else {
        console.log("✅ Car saved:", result);
        await fetchRides();
        setShowCarForm(false);
        setNewCar({ make: "", model: "", colour: "", registration: "", drivetrain: "" });
      }
    } catch (err) {
      console.error("❌ Error sending save car request:", err);
      setError("Something went wrong while saving");
    }
  };

  const handleSaveWatercraft = async () => {
    console.log("🛥️ Attempting to save watercraft:", newWatercraft);

    try {
      const response = await fetch("/rides", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "boat",
          boat_type: newWatercraft.boat_type,
          description: newWatercraft.description || null,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ Failed to save watercraft:", result.error);
        setError(result.error || "Failed to save watercraft");
      } else {
        console.log("✅ Watercraft saved:", result);
        await fetchRides();
        setShowWatercraftForm(false);
        setNewWatercraft({ boat_type: "", description: "" });
      }
    } catch (err) {
      console.error("❌ Error sending save watercraft request:", err);
      setError("Something went wrong while saving");
    }
  };

  const handleSaveTrailer = async () => {
    console.log("🚛 Attempting to save trailer:", newTrailer);

    try {
      const response = await fetch("/rides", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "trailer",
          trailer_type: newTrailer.trailer_type,
          description: newTrailer.description || null,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ Failed to save trailer:", result.error);
        setError(result.error || "Failed to save trailer");
      } else {
        console.log("✅ Trailer saved:", result);
        await fetchRides();
        setShowTrailerForm(false);
        setNewTrailer({ trailer_type: "", description: "" });
      }
    } catch (err) {
      console.error("❌ Error sending save trailer request:", err);
      setError("Something went wrong while saving");
    }
  };

  useEffect(() => {
    fetchRides();
  }, []);

  const getDrivetrainImage = (drivetrain) => {
    switch (drivetrain) {
      case "2 Wheel Drive": return "/2wd-image.png";
      case "All Wheel Drive": return "/awd-image.png";
      case "4 Wheel Drive": return "/4wd-image.png";
      default: return null;
    }
  };

  const renderRideBadge = (ride, idx) => (
    <div
      key={ride.id || idx}
      style={{
        backgroundColor: "#fff",
        color: "#2c7373",
        padding: "16px",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        position: "relative",
        textAlign: "left",
        display: "flex",
        flexDirection: "column", // Stack content vertically
        minHeight: "120px", // Minimum height to accommodate the image
      }}
    >
      {editingRideId !== ride.id && (
        <>
          {ride.type === "car" && getDrivetrainImage(ride.drivetrain) && (
            <img
              src={getDrivetrainImage(ride.drivetrain)}
              alt={ride.drivetrain}
              style={{
                position: "absolute",
                top: "12px",
                right: "44px",
                width: "120px",
                height: "120px",
                objectFit: "contain"
              }}
            />
          )}
  
          {ride.type === "trailer" && (
            <>
              {ride.trailer_type === "Camper Trailer" && (
                <img
                  src="/camp-trailer-image.png"
                  alt={ride.trailer_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
              {ride.trailer_type=== "Caravan" && (
                <img
                  src="/caravan-image.png"
                  alt={ride.trailer_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
              {ride.trailer_type === "Other" && (
                <img
                  src="/trailer-image.png"
                  alt={ride.trailer_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
            </>
          )}
  
          {ride.type === "boat" && (
            <>
              {ride.boat_type === "Powerboat" && (
                <img
                  src="/powerboat-image.png"
                  alt={ride.boat_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
              {ride.boat_type === "Sailboat" && (
                <img
                  src="/sailboat-image.png"
                  alt={ride.boat_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
              {ride.boat_type === "Paddleboat" && (
                <img
                  src="/paddleboat-image.png"
                  alt={ride.boat_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
              {ride.boat_type === "Other" && (
                <img
                  src="/watercraft-image.png"
                  alt={ride.boat_type}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "44px",
                    width: "120px",
                    height: "120px",
                    objectFit: "contain"
                  }}
                />
              )}
            </>
          )}
        </>
      )}
  
      <img
        src="/edit-icon.png"
        alt="Edit"
        style={{ position: "absolute", top: "12px", right: "12px", width: "20px", height: "20px", cursor: "pointer" }}
        onClick={() => {
          setEditingRideId(ride.id);
          setEditedRide({ ...ride });
        }}
      />
      <img
        src="/delete-icon.png"
        alt="Delete"
        style={{ position: "absolute", top: "40px", right: "12px", width: "20px", height: "20px", cursor: "pointer" }}
        onClick={() => deleteRide(ride.id)}
      />
  
      {editingRideId !== ride.id ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          {ride.type === "car" && (
            <>
              <div><strong>Make:</strong> {ride.make}</div>
              <div><strong>Model:</strong> {ride.model}</div>
              <div><strong>Colour:</strong> {ride.colour}</div>
              {ride.registration && <div><strong>Registration:</strong> {ride.registration}</div>}
              <div><strong>Drivetrain:</strong> {ride.drivetrain}</div>
            </>
          )}
          {ride.type === "boat" && (
            <>
              <div><strong>Type:</strong> {ride.boat_type}</div>
              {ride.description && <div><strong>Description:</strong> {ride.description}</div>}
            </>
          )}
          {ride.type === "trailer" && (
            <>
              <div><strong>Type:</strong> {ride.trailer_type}</div>
              {ride.description && <div><strong>Description:</strong> {ride.description}</div>}
            </>
          )}
        </div>
      ) : (
        <div className="login-form" style={{ marginTop: "16px", padding: "20px", width: "100%", boxSizing: "border-box", backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
          {editedRide.type === "car" && (
            <>
              <input type="text" placeholder="Make" value={editedRide.make || ""} onChange={(e) => setEditedRide({ ...editedRide, make: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
              <input type="text" placeholder="Model" value={editedRide.model || ""} onChange={(e) => setEditedRide({ ...editedRide, model: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
              <input type="text" placeholder="Colour" value={editedRide.colour || ""} onChange={(e) => setEditedRide({ ...editedRide, colour: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
              <input type="text" placeholder="Registration" value={editedRide.registration || ""} onChange={(e) => setEditedRide({ ...editedRide, registration: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
              <select value={editedRide.drivetrain || ""} onChange={(e) => setEditedRide({ ...editedRide, drivetrain: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: editedRide.drivetrain ? "#000" : "#888", marginBottom: "8px" }}>
                <option value="" disabled>Select drivetrain</option>
                <option value="2 Wheel Drive">2 Wheel Drive</option>
                <option value="All Wheel Drive">All Wheel Drive</option>
                <option value="4 Wheel Drive">4 Wheel Drive</option>
              </select>
            </>
          )}
          {editedRide.type === "boat" && (
            <>
              <select value={editedRide.boat_type || ""} onChange={(e) => setEditedRide({ ...editedRide, boat_type: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: editedRide.boat_type ? "#000" : "#888", marginBottom: "8px" }}>
                <option value="" disabled>Select Type</option>
                <option value="Powerboat">Powerboat</option>
                <option value="Sailboat">Sailboat</option>
                <option value="Paddleboat">Paddleboat</option>
                <option value="Other">Other</option>
              </select>
              <input type="text" placeholder="Description (optional)" value={editedRide.description || ""} onChange={(e) => setEditedRide({ ...editedRide, description: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
            </>
          )}
          {editedRide.type === "trailer" && (
            <>
              <select value={editedRide.trailer_type || ""} onChange={(e) => setEditedRide({ ...editedRide, trailer_type: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: editedRide.trailer_type ? "#000" : "#888", marginBottom: "8px" }}>
                <option value="" disabled>Select Type</option>
                <option value="Caravan">Caravan</option>
                <option value="Camper Trailer">Camper Trailer</option>
                <option value="Other">Other</option>
              </select>
              <input type="text" placeholder="Description (optional)" value={editedRide.description || ""} onChange={(e) => setEditedRide({ ...editedRide, description: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
            </>
          )}
          <button type="button" onClick={saveEditedRide} className={`login-button ${editedRide.type === 'car' ? (editedRide.make && editedRide.model && editedRide.colour && editedRide.drivetrain ? "" : "disabled-button") : editedRide.type === 'boat' ? (editedRide.boat_type ? "" : "disabled-button") : editedRide.type === 'trailer' ? (editedRide.trailer_type ? "" : "disabled-button") : ""}`} disabled={editedRide.type === 'car' ? (!editedRide.make || !editedRide.model || !editedRide.colour || !editedRide.drivetrain) : editedRide.type === 'boat' ? (!editedRide.boat_type) : editedRide.type === 'trailer' ? (!editedRide.trailer_type) : false}>Save</button>
          <button type="button" onClick={() => { setEditedRide({}); setEditingRideId(null); }} className="login-button" style={{ marginTop: "8px", backgroundColor: "#ccc", color: "#333" }}>Cancel</button>
        </div>
      )}
    </div>
  );
  return (
    <div className="login-container">
      <div className="login-box">
      <img
          src="/Rides-image.png"
          alt="Gear"
          style={{ width: "240px", margin: "0 auto 20px auto", display: "block" }}
        />
        <h2>Rides</h2>
        {loading && <p>Loading...</p>}
        {error && <div className="error-message">{error}</div>}
  
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "30px" }}>
          {/* Car Badges */}
          {rides.filter(ride => ride.type === 'car').map(renderRideBadge)}
  
          {/* Add Car Button */}
          <div className="login-form" style={{ marginTop: "20px" }}>
            <button type="button" onClick={() => setShowCarForm(!showCarForm)} style={{ width: "100%" }}>Add Car</button>
            {showCarForm && (
              <div className="login-form" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", marginTop: "10px", marginBottom: "10px", padding: "20px", width: "100%", boxSizing: "border-box" }}>
                <input type="text" placeholder="Make" value={newCar.make} onChange={(e) => setNewCar({ ...newCar, make: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <input type="text" placeholder="Model" value={newCar.model} onChange={(e) => setNewCar({ ...newCar, model: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <input type="text" placeholder="Colour" value={newCar.colour} onChange={(e) => setNewCar({ ...newCar, colour: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <input type="text" placeholder="Registration (optional)" value={newCar.registration} onChange={(e) => setNewCar({ ...newCar, registration: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <select value={newCar.drivetrain} onChange={(e) => setNewCar({ ...newCar, drivetrain: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: newCar.drivetrain ? "#000" : "#888", marginBottom: "8px" }}>
                  <option value="" disabled>Select drivetrain</option>
                  <option value="2 Wheel Drive">2 Wheel Drive</option>
                  <option value="All Wheel Drive">All Wheel Drive</option>
                  <option value="4 Wheel Drive">4 Wheel Drive</option>
                </select>
                <button type="button" onClick={handleSaveCar} className={`login-button ${newCar.make && newCar.model && newCar.colour && newCar.drivetrain ? "" : "disabled-button"}`} disabled={!newCar.make || !newCar.model || !newCar.colour || !newCar.drivetrain}>Save Car</button>
                <button type="button" onClick={() => setShowCarForm(false)} className="login-button" style={{ marginTop: "8px", backgroundColor: "#ccc", color: "#333" }}>Cancel</button>
              </div>
            )}
          </div>
  
          {/* Trailer Badges */}
          {rides.filter(ride => ride.type === 'trailer').map(renderRideBadge)}
  
          {/* Add Caravan or Trailer Button */}
          <div className="login-form" style={{ marginTop: "30px" }}>
            <button type="button" onClick={() => setShowTrailerForm(!showTrailerForm)} style={{ width: "100%" }}>Add Caravan or Trailer</button>
            {showTrailerForm && (
              <div className="login-form" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", marginTop: "10px", marginBottom: "10px", padding: "20px", width: "100%", boxSizing: "border-box" }}>
                <select value={newTrailer.trailer_type} onChange={(e) => setNewTrailer({ ...newTrailer, trailer_type: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: newTrailer.trailer_type ? "#000" : "#888", marginBottom: "8px" }}>
                  <option value="" disabled>Select Type</option>
                  <option value="Caravan">Caravan</option>
                  <option value="Camper Trailer">Camper Trailer</option>
                  <option value="Other">Other</option>
                </select>
                <input type="text" placeholder="Description (optional)" value={newTrailer.description} onChange={(e) => setNewTrailer({ ...newTrailer, description: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <button type="button" onClick={handleSaveTrailer} className={`login-button ${newTrailer.trailer_type ? "" : "disabled-button"}`} disabled={!newTrailer.trailer_type}>Save Trailer</button>
                <button type="button" onClick={() => setShowTrailerForm(false)} className="login-button" style={{ marginTop: "8px", backgroundColor: "#ccc", color: "#333" }}>Cancel</button>
              </div>
            )}
          </div>
  
          {/* Watercraft Badges */}
          {rides.filter(ride => ride.type === 'boat').map(renderRideBadge)}
  
          {/* Add Watercraft Button */}
          <div className="login-form" style={{ marginTop: "30px" }}>
            <button type="button" onClick={() => setShowWatercraftForm(!showWatercraftForm)} style={{ width: "100%" }}>Add Watercraft</button>
            {showWatercraftForm && (
              <div className="login-form" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", marginTop: "10px", marginBottom: "10px", padding: "20px", width: "100%", boxSizing: "border-box" }}>
                <select value={newWatercraft.boat_type} onChange={(e) => setNewWatercraft({ ...newWatercraft, boat_type: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: newWatercraft.boat_type ? "#000" : "#888", marginBottom: "8px" }}>
                  <option value="" disabled>Select Type</option>
                  <option value="Powerboat">Powerboat</option>
                  <option value="Sailboat">Sailboat</option>
                  <option value="Paddleboat">Paddleboat</option>
                  <option value="Other">Other</option>
                </select>
                <input type="text" placeholder="Description (optional)" value={newWatercraft.description} onChange={(e) => setNewWatercraft({ ...newWatercraft, description: e.target.value })} style={{ border: "1px solid #ccc", marginBottom: "8px" }} />
                <button type="button" onClick={handleSaveWatercraft} className={`login-button ${newWatercraft.boat_type ? "" : "disabled-button"}`} disabled={!newWatercraft.boat_type}>Save Watercraft</button>
                <button type="button" onClick={() => setShowWatercraftForm(false)} className="login-button" style={{ marginTop: "8px", backgroundColor: "#ccc", color: "#333" }}>Cancel</button>
              </div>
            )}
          </div>
        </div>
  
        <button className="home-button" onClick={() => navigate("/")} style={{ marginTop: "30px", width: "100%" }}>Home</button>
        <div className="version-text" style={{marginTop: "20px", fontSize: "12px", color: "#ccc", display: "flex", justifyContent: "space-between" }}>
        <span>Version: <strong>{APP_VERSION}</strong></span>
        <div className="footer-links">
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
              }}
              className="footer-link"
            >
              Logout
            </Link>

            <Link to="/contact" className="footer-link">
              Contact us
            </Link>
          </div>
      </div>
    </div>
  </div>
);
}